import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  NavbarBackLink,
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
} from 'konsta/react'
import {
  DollarSign,
  TrendingUp,
  DoorOpen,
  Users,
  CircleDot,
  Clock,
  Calendar,
  Percent,
  Crown,
  Gamepad2,
  BarChart3,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime } from '@/lib/utils'

// --- Mock Data ---

const overviewStats = [
  {
    label: '總佣金收入',
    value: 345000,
    icon: DollarSign,
    color: '#007AFF',
    bg: '#007AFF',
  },
  {
    label: '本月佣金',
    value: 68000,
    icon: TrendingUp,
    color: '#34C759',
    bg: '#34C759',
  },
  {
    label: '活躍房間',
    value: 3,
    icon: DoorOpen,
    color: '#FF9500',
    bg: '#FF9500',
    isCurrency: false,
  },
  {
    label: '總玩家數',
    value: 47,
    icon: Users,
    color: '#AF52DE',
    bg: '#AF52DE',
    isCurrency: false,
  },
]

const recentCommissions = [
  { id: '1', room: '高額桌 VIP', amount: 12500, time: new Date(Date.now() - 1000 * 60 * 15), handId: 'H041' },
  { id: '2', room: 'David的私人桌', amount: 3200, time: new Date(Date.now() - 1000 * 60 * 45), handId: 'H039' },
  { id: '3', room: '高額桌 VIP', amount: 8700, time: new Date(Date.now() - 1000 * 60 * 90), handId: 'H037' },
  { id: '4', room: '週末錦標賽', amount: 5500, time: new Date(Date.now() - 1000 * 60 * 180), handId: 'H034' },
  { id: '5', room: 'David的私人桌', amount: 2800, time: new Date(Date.now() - 1000 * 60 * 300), handId: 'H030' },
]

const agentRooms = [
  {
    id: '1',
    name: '高額桌 VIP',
    blinds: '$25/$50',
    players: '8/9',
    status: 'active' as const,
    commission: 2.5,
    totalEarned: 185000,
  },
  {
    id: '2',
    name: 'David的私人桌',
    blinds: '$5/$10',
    players: '4/6',
    status: 'active' as const,
    commission: 2.0,
    totalEarned: 92000,
  },
  {
    id: '3',
    name: '週末錦標賽',
    blinds: '$10/$20',
    players: '6/9',
    status: 'waiting' as const,
    commission: 3.0,
    totalEarned: 68000,
  },
]

const commissionHistory = [
  { id: '1', date: new Date(Date.now() - 1000 * 60 * 60 * 2), room: '高額桌 VIP', amount: 12500, rate: 2.5 },
  { id: '2', date: new Date(Date.now() - 1000 * 60 * 60 * 5), room: 'David的私人桌', amount: 3200, rate: 2.0 },
  { id: '3', date: new Date(Date.now() - 1000 * 60 * 60 * 12), room: '高額桌 VIP', amount: 8700, rate: 2.5 },
  { id: '4', date: new Date(Date.now() - 1000 * 60 * 60 * 24), room: '週末錦標賽', amount: 5500, rate: 3.0 },
  { id: '5', date: new Date(Date.now() - 1000 * 60 * 60 * 36), room: '高額桌 VIP', amount: 15200, rate: 2.5 },
  { id: '6', date: new Date(Date.now() - 1000 * 60 * 60 * 72), room: 'David的私人桌', amount: 4100, rate: 2.0 },
  { id: '7', date: new Date(Date.now() - 1000 * 60 * 60 * 120), room: '週末錦標賽', amount: 6800, rate: 3.0 },
  { id: '8', date: new Date(Date.now() - 1000 * 60 * 60 * 168), room: '高額桌 VIP', amount: 9300, rate: 2.5 },
]

const agentPlayers = [
  { id: '1', name: 'Mike Chen', games: 128, rakeGenerated: 64500, avatar: 'MC' },
  { id: '2', name: '王小明', games: 95, rakeGenerated: 47200, avatar: '王' },
  { id: '3', name: 'Sarah L.', games: 82, rakeGenerated: 38900, avatar: 'SL' },
  { id: '4', name: '張大衛', games: 67, rakeGenerated: 31200, avatar: '張' },
  { id: '5', name: 'Alex Wong', games: 54, rakeGenerated: 25800, avatar: 'AW' },
  { id: '6', name: '李美琪', games: 41, rakeGenerated: 18500, avatar: '李' },
]

// --- Components ---

type Tab = 'overview' | 'rooms' | 'commission' | 'players'

function OverviewTab() {
  return (
    <>
      {/* Stats Grid */}
      <Block className="!px-4 !mt-4">
        <div className="grid grid-cols-2 gap-3">
          {overviewStats.map((stat) => {
            const Icon = stat.icon
            return (
              <div
                key={stat.label}
                className="bg-white rounded-2xl p-4 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${stat.bg}15` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: stat.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-black">
                  {stat.isCurrency === false
                    ? stat.value
                    : formatCurrency(stat.value)}
                </p>
                <p className="text-xs text-[#8E8E93] mt-0.5">{stat.label}</p>
              </div>
            )
          })}
        </div>
      </Block>

      {/* Recent Commissions */}
      <BlockTitle>最近佣金</BlockTitle>
      <List strongIos insetIos>
        {recentCommissions.map((item) => (
          <ListItem
            key={item.id}
            title={item.room}
            subtitle={
              <span className="flex items-center gap-1 text-[#8E8E93]">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(item.time)}
              </span>
            }
            after={
              <span className="text-sm font-semibold text-[#34C759]">
                +{formatCurrency(item.amount)}
              </span>
            }
            media={
              <div className="w-10 h-10 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#34C759]" />
              </div>
            }
            text={`手牌 ${item.handId}`}
          />
        ))}
      </List>
    </>
  )
}

function RoomsTab() {
  return (
    <>
      <BlockTitle>我的代理房間</BlockTitle>
      <List strongIos insetIos>
        {agentRooms.map((room) => (
          <ListItem
            key={room.id}
            link
            chevronIos
            title={room.name}
            subtitle={`盲注 ${room.blinds} | 佣金 ${room.commission}%`}
            after={
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  room.status === 'active'
                    ? 'bg-[#34C759]/10 text-[#34C759]'
                    : 'bg-[#FF9500]/10 text-[#FF9500]'
                }`}
              >
                {room.status === 'active' ? '進行中' : '等待中'}
              </span>
            }
            media={
              <div className="w-10 h-10 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
                <DoorOpen className="w-5 h-5 text-[#007AFF]" />
              </div>
            }
            text={
              <span className="flex items-center gap-3 text-[#8E8E93]">
                <span className="flex items-center gap-1">
                  <CircleDot className="w-3 h-3" />
                  {room.players} 玩家
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  累計 {formatCurrency(room.totalEarned)}
                </span>
              </span>
            }
          />
        ))}
      </List>

      {/* Room Summary */}
      <Block strong inset className="!mt-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#8E8E93]">總活躍房間</span>
          <span className="font-semibold text-black">
            {agentRooms.filter((r) => r.status === 'active').length} / {agentRooms.length}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-[#8E8E93]">總在線玩家</span>
          <span className="font-semibold text-black">
            {agentRooms.reduce((sum, r) => sum + parseInt(r.players.split('/')[0]), 0)} 人
          </span>
        </div>
      </Block>
    </>
  )
}

function CommissionTab() {
  return (
    <>
      {/* Summary card */}
      <Block strong inset className="!mt-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-[#34C759]" />
          </div>
          <div>
            <p className="text-xs text-[#8E8E93]">近 7 天佣金</p>
            <p className="text-xl font-bold text-black">
              {formatCurrency(
                commissionHistory.reduce((sum, c) => sum + c.amount, 0)
              )}
            </p>
          </div>
        </div>
        <div className="h-px bg-[#E5E5EA] my-3" />
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#8E8E93]">平均佣金率</span>
          <span className="font-semibold text-black">2.5%</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1.5">
          <span className="text-[#8E8E93]">總交易筆數</span>
          <span className="font-semibold text-black">{commissionHistory.length} 筆</span>
        </div>
      </Block>

      <BlockTitle>佣金明細</BlockTitle>
      <List strongIos insetIos>
        {commissionHistory.map((item) => (
          <ListItem
            key={item.id}
            title={item.room}
            subtitle={
              <span className="flex items-center gap-1 text-[#8E8E93]">
                <Calendar className="w-3 h-3" />
                {formatRelativeTime(item.date)}
              </span>
            }
            after={
              <span className="text-sm font-semibold text-[#34C759]">
                +{formatCurrency(item.amount)}
              </span>
            }
            media={
              <div className="w-10 h-10 rounded-xl bg-[#FF9500]/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-[#FF9500]" />
              </div>
            }
            text={`佣金率 ${item.rate}%`}
          />
        ))}
      </List>
    </>
  )
}

function PlayersTab() {
  return (
    <>
      <BlockTitle>我的玩家</BlockTitle>
      <List strongIos insetIos>
        {agentPlayers.map((player, index) => (
          <ListItem
            key={player.id}
            title={
              <span className="flex items-center gap-2">
                {player.name}
                {index === 0 && (
                  <Crown className="w-3.5 h-3.5 text-[#FF9500]" />
                )}
              </span>
            }
            subtitle={
              <span className="flex items-center gap-1 text-[#8E8E93]">
                <Gamepad2 className="w-3 h-3" />
                {player.games} 局遊戲
              </span>
            }
            after={
              <span className="text-sm font-semibold text-[#007AFF]">
                {formatCurrency(player.rakeGenerated)}
              </span>
            }
            media={
              <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                <span className="text-sm font-bold text-[#007AFF]">
                  {player.avatar}
                </span>
              </div>
            }
            text={
              <span className="text-[#8E8E93]">
                貢獻抽水 {formatCurrency(player.rakeGenerated)}
              </span>
            }
          />
        ))}
      </List>

      {/* Player summary */}
      <Block strong inset className="!mt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#AF52DE]/10 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#AF52DE]" />
          </div>
          <div>
            <p className="text-xs text-[#8E8E93]">玩家總貢獻</p>
            <p className="text-lg font-bold text-black">
              {formatCurrency(
                agentPlayers.reduce((sum, p) => sum + p.rakeGenerated, 0)
              )}
            </p>
          </div>
        </div>
      </Block>
    </>
  )
}

// --- Main Screen ---

export function AgentDashboardScreen() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: '總覽' },
    { key: 'rooms', label: '房間' },
    { key: 'commission', label: '佣金' },
    { key: 'players', label: '玩家' },
  ]

  return (
    <Page className="bg-[#F2F2F7]">
      <Navbar
        title="代理中心"
        left={<NavbarBackLink onClick={() => navigate(-1)} />}
      />

      {/* Segmented Tabs */}
      <Block className="!px-4 !mt-2 !mb-0">
        <Segmented strong rounded>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {/* Tab Content */}
      <div className="overflow-y-auto pb-8">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'rooms' && <RoomsTab />}
        {activeTab === 'commission' && <CommissionTab />}
        {activeTab === 'players' && <PlayersTab />}
      </div>
    </Page>
  )
}
